import {FC, useEffect, useState} from 'react';
import {Box, Checkbox, FormControlLabel, Typography} from '@mui/material';
import {Form, Formik, FormikConfig, FormikProps} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import {authApi} from 'src/api';
import {loginInitialValues} from '../formData/common/initialData';
import {loginSchema, LoginType} from '../formData/login';
import {AMPInput} from 'src/components/common/AMPInput';
import {getHelperText} from 'src/utils/forms';
import {useAppDispatch} from 'src/app/hooks';
import {Link, useNavigate} from 'react-router-dom';
import {AppDispatch} from 'src/types';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {changeLang, preloaderChangeStatus, setActiveBusiness,} from 'src/stores/common/commonSlice';
import CryptoJs from 'crypto-js';
import {iamApi} from 'src/api/iam';
import {cryptoKey} from 'src/axiosBaseQuery/interceprtors';
import {NotifyError} from 'src/utils/notification';
import {
    ContractStatusEnum,
    LanguageEnum,
    LanguageShortEnum,
    RegistrationStatusEnum,
} from 'src/enums';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {UserTypeEnum} from 'src/enums/common';
import {actionsWrapperSx, LoginFormSx} from './LoginForm.sx';

type LoginMutationTrigger = ReturnType<typeof authApi.useLoginMutation>[0];

const handleSubmit =
    (
        trigger: LoginMutationTrigger,
        dispatch: AppDispatch,
        remember = false
    ): FormikConfig<LoginType>['onSubmit'] =>
        async ({emailAddress, password}) => {
            dispatch(preloaderChangeStatus(true));
            try {
                const crypto = CryptoJs.AES.encrypt(
                    JSON.stringify(remember),
                    cryptoKey.toString()
                ).toString();
                sessionStorage.setItem('remember', JSON.stringify(true));
                localStorage.setItem('remember', crypto);
                const result = await trigger({
                    emailAddress,
                    password,
                }).unwrap();
                const languagesMap = {
                    [LanguageEnum.ENGLISH]: LanguageShortEnum.EN,
                    [LanguageEnum.FRENCH]: LanguageShortEnum.FR,
                    [LanguageEnum.DEUTSCH]: LanguageShortEnum.DE,
                    [LanguageEnum.UKRAINIAN]: LanguageShortEnum.UK,
                };
                dispatch(changeLang(languagesMap[result.payload.language ?? LanguageEnum.ENGLISH]));
                // Ask again for what this account may do. The query is keyed on the user id, and the id
                // survives in storage from the previous session - so it had already run on the login
                // page, without a token, and been answered 401. Signing in as that same person changes
                // no argument, so nothing refetched: the cached refusal stood, the permission set stayed
                // empty, and the app rendered a signed-in user with no menu and no page until somebody
                // reloaded by hand.
                dispatch(iamApi.util.invalidateTags(['Me']));
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const LoginForm: FC = () => {
    const intl = useIntl();

    const [trigger, response] = authApi.useLoginMutation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [remember, setRemember] = useState<boolean>(false);

    useEffect(() => {
        if (response.data) {
            const registrationStatus = response.data?.payload?.registrationStatus;
            const profileActive = registrationStatus === RegistrationStatusEnum.ACTIVATED;
            response.data.payload.business &&
            dispatch(setActiveBusiness(response.data.payload.business.id));
            // Landing is decided by what this account HAS, not by what it is called.
            //
            // The role branches this replaces had drifted from the two other copies of the same
            // decision (App.tsx and routes.tsx) and could not survive business-defined role names.
            // What survives is the part that was never about roles: someone with a live contract and a
            // charger wants their charger, and someone still being set up wants the welcome page.
            const payload = response.data.payload;
            const operational = payload.contracts?.find(
                (contract) => contract.status === ContractStatusEnum.OPERATION
            );

            // Only a private driver. The shortcut exists because a driver with a live contract and a
            // charger wants that charger, not a menu — but a company has staff, sites and
            // chargers, and sending it to a single contract because one happened to be operational
            // dropped it on a page it did not ask for. Everyone else falls through to the router,
            // which lands them on the first page their permissions allow.
            const isPrivateDriver = payload.userType === UserTypeEnum.PRIVATE_DRIVER;

            if (operational && profileActive && isPrivateDriver) {
                if (payload.chargePoint) {
                    return navigate(
                        AppRouteSpace([
                            AppRouteEnum.app,
                            AppRouteEnum.chargers,
                            String(payload.chargePoint.id),
                        ])
                    );
                }
                if (payload.location) {
                    return navigate(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations]));
                }
                return navigate(
                    AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts, String(operational.id)])
                );
            }

            // Everyone else: hand the decision to the router rather than guess here. Permissions have
            // not arrived yet at this point, and the index route waits for them before choosing -
            // which is the difference between landing somewhere and landing somewhere twice.
            navigate(AppRouteSpace([AppRouteEnum.app]));
        }
    }, [response]);
    return (
        <Formik
            initialValues={loginInitialValues}
            validationSchema={loginSchema}
            onSubmit={handleSubmit(trigger, dispatch, remember)}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<LoginType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={LoginFormSx}>
                        <AMPInput
                            value={values['emailAddress']}
                            type={'emailAddress'}
                            label={'emailAddress'}
                            input={'emailAddress'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            hasError={touched['emailAddress'] && Boolean(errors['emailAddress'])}
                            helperText={getHelperText('emailAddress', touched, errors, intl)}
                            placeholder="emailPlaceholder"
                        />
                        <AMPInput
                            value={values['password'] as string}
                            type={'password'}
                            label={'password'}
                            input={'password'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            hasError={touched['password'] && Boolean(errors['password'])}
                            helperText={getHelperText('password', touched, errors, intl)}
                            placeholder="passwordPlaceholder"
                        />
                        <FormControlLabel
                            label={intl.formatMessage({id: 'remember'})}
                            control={
                                <Checkbox
                                    color="secondary"
                                    value={remember}
                                    onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                                        setRemember(checked)
                                    }
                                />
                            }
                        />
                        <Box sx={actionsWrapperSx}>
                            <Link to="/forgot">
                                <Typography
                                    variant="body1"
                                    color="primary"
                                >
                                    <FormattedMessage id="forgotPassword"/>
                                </Typography>
                            </Link>

                            <SubmitButton
                                text="login"
                                isSubmitting={isSubmitting}
                                rounding
                                color="brand"
                            />
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
